// Moteur d'IA — chaque organisme branche le sien.
//
// La plateforme ne fournit plus de clé mutualisée : les fonctions d'IA
// n'existent que si l'organisme connecte son propre moteur. Toute API parlant
// l'interface d'OpenAI convient ; l'adresse vide vise OpenAI.
import React, { useState, useEffect } from 'react';
import { KeyRound } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

const CHAMP =
  'w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[13px] outline-none focus:border-[#C7D2FE]';

export default function IaIntegrationCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ api_key: '', model: '', base_url: '' });

  const charger = async () => {
    setLoading(true);
    try {
      const { data: vue } = await api.get('/integrations/ia');
      setData(vue);
      setForm({ api_key: '', model: vue.model || '', base_url: vue.base_url || '' });
    } catch (err) {
      const d = err?.response?.data?.detail || "Impossible de charger la configuration de l'IA";
      toast({ title: 'Erreur', description: d, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    charger();
  }, []);

  const enregistrer = async () => {
    setSaving(true);
    try {
      const payload = { model: form.model, base_url: form.base_url };
      if (form.api_key.trim()) payload.api_key = form.api_key.trim();
      const { data: maj } = await api.put('/integrations/ia', payload);
      setData(maj);
      setForm((f) => ({ ...f, api_key: '' }));
      toast({ title: 'Enregistré', description: "Le moteur d'IA est configuré." });
    } catch (err) {
      const d = err?.response?.data?.detail || 'Enregistrement impossible';
      toast({ title: 'Erreur', description: d, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const essayer = async () => {
    setTesting(true);
    try {
      const { data: r } = await api.post('/integrations/ia/test');
      toast({ title: 'Le moteur répond', description: r.reponse || 'Réponse reçue.' });
    } catch (err) {
      const d = err?.response?.data?.detail || "Le moteur n'a pas répondu";
      toast({ title: 'Échec', description: d, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const deconnecter = async () => {
    try {
      await api.delete('/integrations/ia');
      toast({ title: 'Déconnecté', description: "Les fonctions d'IA sont désactivées." });
      charger();
    } catch (err) {
      const d = err?.response?.data?.detail || 'Suppression impossible';
      toast({ title: 'Erreur', description: d, variant: 'destructive' });
    }
  };

  const connecte = !!data?.configured;

  return (
    <div className="soft-card p-5" data-testid="ia-integration-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#EDE8FF] flex items-center justify-center">
            <KeyRound className="w-4 h-4 text-[#4F46E5]" />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-[#1A1B2E]">Intelligence artificielle</div>
            <div className="text-[12.5px] text-[#8E8FA5]">Votre propre moteur, votre propre clé</div>
          </div>
        </div>
        <span
          className={`h-7 px-2.5 rounded-md text-[11px] font-semibold flex items-center ${
            connecte ? 'bg-[#E6FBF7] text-[#0F766E]' : 'bg-[#F3F4F8] text-[#6B6C85]'
          }`}
        >
          {connecte ? 'Connecté' : 'Non connecté'}
        </span>
      </div>

      <div className="text-[12.5px] text-[#6B6C85] mt-3 mb-4">
        L&apos;assistante, la génération de programme et l&apos;adaptation des modèles Qualiopi
        utilisent le moteur branché ici. Tant qu&apos;aucun n&apos;est configuré, ces fonctions
        restent inactives. Toute API compatible OpenAI convient — laissez l&apos;adresse vide
        pour OpenAI.
      </div>

      {loading ? (
        <div className="text-[12.5px] text-[#8E8FA5]">Chargement…</div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">
              Clé d&apos;API
              {connecte && (
                <span className="font-normal text-[#8E8FA5]"> — actuelle : {data.key_masked}</span>
              )}
            </label>
            <input
              data-testid="ia-key"
              type="password"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder={connecte ? 'Laisser vide pour conserver la clé actuelle' : 'sk-…'}
              className={CHAMP}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">Modèle</label>
              <input
                data-testid="ia-model"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                placeholder="gpt-4o-mini"
                className={CHAMP}
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">
                Adresse de l&apos;API <span className="font-normal text-[#8E8FA5]">— optionnel</span>
              </label>
              <input
                data-testid="ia-base-url"
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                placeholder="https://api.openai.com/v1"
                className={CHAMP}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              data-testid="ia-save"
              onClick={enregistrer}
              disabled={saving}
              className="h-9 px-3.5 rounded-xl bg-[#4F46E5] text-white text-[13px] font-semibold flex items-center gap-1.5 hover:opacity-95 disabled:opacity-60"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            {connecte && (
              <>
                <button
                  data-testid="ia-test"
                  onClick={essayer}
                  disabled={testing}
                  className="h-9 px-3.5 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:bg-[#F6F7FB] disabled:opacity-60"
                >
                  {testing ? 'Essai…' : 'Tester'}
                </button>
                <button
                  data-testid="ia-disconnect"
                  onClick={deconnecter}
                  className="text-[12px] font-semibold text-[#B91C1C] hover:underline ml-auto"
                >
                  Déconnecter
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
